"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Item = { title: string; body: string; icon?: ReactNode; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson"; span?: "sm" | "md" | "lg" | "wide" | "tall" };

const ACCENT_SOFT: Record<NonNullable<Item["accent"]>, string> = {
  red: "from-red-500/10 to-red-500/0", coral: "from-coral-500/10 to-coral-500/0", purple: "from-purple-500/10 to-purple-500/0", cyan: "from-cyan-500/10 to-cyan-500/0", denim: "from-denim-500/10 to-denim-500/0", emerald: "from-emerald-500/10 to-emerald-500/0", lime: "from-lime-500/10 to-lime-500/0", "amber-gold": "from-amber-gold-500/10 to-amber-gold-500/0", crimson: "from-crimson-500/10 to-crimson-500/0",
};
const ACCENT_TEXT: Record<NonNullable<Item["accent"]>, string> = {
  red: "text-red-400", coral: "text-coral-400", purple: "text-purple-400", cyan: "text-cyan-400", denim: "text-denim-400", emerald: "text-emerald-400", lime: "text-lime-400", "amber-gold": "text-amber-gold-400", crimson: "text-crimson-400",
};

export function BentoBlock({ items, title, eyebrow, description }: { items: Item[]; title?: string; eyebrow?: string; description?: string }) {
  return (
    <div className="space-y-10">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className="grid auto-rows-[180px] grid-cols-2 gap-4 lg:grid-cols-4 lg:grid-rows-[200px]">
        {items.map((item, i) => {
          const accent = item.accent ?? "purple";
          const spanClass = { sm: "col-span-1 row-span-1", md: "col-span-2 row-span-1", lg: "col-span-2 row-span-2", wide: "col-span-2 row-span-1", tall: "col-span-1 row-span-2" }[item.span ?? "sm"];
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.05 }} className={cn("glass-card group relative overflow-hidden rounded-2xl border border-border p-5 lg:p-6", spanClass)}>
              <div className={cn("absolute inset-0 bg-gradient-to-br opacity-60 transition-opacity group-hover:opacity-100", ACCENT_SOFT[accent])} />
              <div className="relative flex h-full flex-col">
                <div className="mb-3 flex items-center gap-2">
                  {item.icon && <div className={cn("flex h-10 w-10 items-center justify-center rounded-xl bg-card", ACCENT_TEXT[accent])}>{item.icon}</div>}
                </div>
                <h3 className="font-rajdhani text-lg font-bold text-foreground lg:text-xl">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80/80 line-clamp-4 lg:line-clamp-5">{item.body}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
